import { roundTo } from '../units/round.js';

/**
 * Gasto energetico (docs/NUTRITION_ENGINE.md §2).
 *
 * El RMR lo estima `profile/anthropometrics.ts`; aqui se monta el TDEE por
 * componentes y, sobre todo, se corrige con lo observado. La formula es solo la
 * hipotesis de partida: manda la calibracion (§2.3).
 */

/** Umbrales unicos del factor NEAT por pasos diarios (§2.2). */
export const NEAT_FACTOR_BY_STEPS: readonly { minSteps: number; factor: number }[] = [
  { minSteps: 11_000, factor: 1.5 },
  { minSteps: 8_000, factor: 1.4 },
  { minSteps: 5_000, factor: 1.3 },
  { minSteps: 0, factor: 1.2 },
];

export const ENERGY_CONSTANTS = {
  /** kcal por minuto de fuerza efectiva. */
  strengthKcalPerMinute: 5,
  /** kcal por kg de peso corporal y km corrido. */
  runKcalPerKgPerKm: 1,
  /**
   * Incertidumbre declarada del TDEE estimado por formula. El §2.2 la fija en
   * ±250 kcal sobre ~2.700, que es un 9 %; se expresa en porcentaje para que no
   * mienta con otros pesos corporales.
   */
  formulaUncertaintyPct: 0.09,
  /** Convencion para tejido mixto (§2.3). Es una aproximacion, y se dice. */
  kcalPerKgTissue: 7700,
} as const;

export function neatFactor(dailySteps: number): number {
  const tramo = NEAT_FACTOR_BY_STEPS.find((entry) => dailySteps >= entry.minSteps);
  // El ultimo tramo empieza en 0, asi que solo un valor negativo llega aqui.
  return tramo?.factor ?? 1.2;
}

/**
 * Los pasos que se suponen cuando no hay suficientes anotados: el tramo
 * intermedio del §2.2 (5.000–7.999, factor 1,3). Inventar un numero alto seria
 * peor que suponer el del medio y decirlo, y la calibracion del §2.3 corrige
 * el error midiendo lo que pasa con el peso.
 */
export const ASSUMED_DAILY_STEPS = 6000;

/**
 * Cuando dejan de suponerse los pasos (§17).
 *
 * - `windowDays`: la ventana mira hacia atras y **termina ayer**. El dia de hoy
 *   no ha acabado, y unos pasos anotados a media tarde tirarian la media hacia
 *   abajo sin que nadie hubiera andado menos.
 * - `minDaysLogged`: con uno o dos dias anotados la media es la de esos dias,
 *   no la de una semana normal. Por debajo se sigue suponiendo, y se dice.
 */
export const STEPS_FOR_NEAT = {
  windowDays: 14,
  minDaysLogged: 4,
} as const;

export interface StepsForNeat {
  steps: number;
  source: 'logged' | 'assumed';
  /** Dias con pasos anotados dentro de la ventana. */
  daysLogged: number;
}

/**
 * Los pasos diarios con los que se calcula el NEAT.
 *
 * Recibe los pasos de los dias ANOTADOS dentro de la ventana, uno por dia; un
 * dia sin anotar no esta, porque no es un dia con cero pasos.
 *
 * Es la MEDIA y no la mediana a proposito: el NEAT es energia, y la energia se
 * suma. La excursion de un domingo gasto lo que gasto; una mediana haria como
 * si no hubiera pasado.
 */
export function stepsForNeat(loggedSteps: readonly number[]): StepsForNeat {
  const daysLogged = loggedSteps.length;
  if (daysLogged < STEPS_FOR_NEAT.minDaysLogged) {
    return { steps: ASSUMED_DAILY_STEPS, source: 'assumed', daysLogged };
  }
  const total = loggedSteps.reduce((suma, pasos) => suma + pasos, 0);
  return { steps: Math.round(total / daysLogged), source: 'logged', daysLogged };
}

export interface TdeeComponentsInput {
  rmrKcal: number;
  bodyWeightKg: number;
  dailySteps: number;
  /** Sesiones de fuerza por semana y minutos efectivos de cada una. */
  strengthSessionsPerWeek: number;
  strengthMinutesPerSession: number;
  /** Kilometros de carrera a la semana. */
  runKmPerWeek: number;
}

export interface TdeeEstimate {
  kcal: number;
  /** Margen declarado: el numero no es un hecho (§2.2). */
  uncertaintyKcal: number;
  breakdown: {
    restingKcal: number;
    neatFactor: number;
    strengthKcal: number;
    cardioKcal: number;
  };
}

/**
 * TDEE por componentes. El efecto termico de los alimentos NO se suma aparte:
 * ya va dentro del factor NEAT, y sumarlo dos veces inflaria el objetivo entre
 * 200 y 300 kcal sin que nada lo delatara.
 */
export function estimateTdeeByComponents(input: TdeeComponentsInput): TdeeEstimate {
  const factor = neatFactor(input.dailySteps);
  const resting = input.rmrKcal * factor;
  const strength =
    (input.strengthSessionsPerWeek *
      input.strengthMinutesPerSession *
      ENERGY_CONSTANTS.strengthKcalPerMinute) /
    7;
  const cardio =
    (input.runKmPerWeek * input.bodyWeightKg * ENERGY_CONSTANTS.runKcalPerKgPerKm) / 7;
  const total = resting + strength + cardio;

  return {
    kcal: Math.round(total),
    uncertaintyKcal: Math.round(total * ENERGY_CONSTANTS.formulaUncertaintyPct),
    breakdown: {
      restingKcal: Math.round(resting),
      neatFactor: factor,
      strengthKcal: Math.round(strength),
      cardioKcal: Math.round(cardio),
    },
  };
}

// --------------------------------------------------------------------------
// Calibracion adaptativa (§2.3)
// --------------------------------------------------------------------------

export const CALIBRATION_RULES = {
  minDays: 14,
  idealDays: 21,
  minAdherence: 0.8,
  minWeightsPerWeek: 5,
  ewmaAlpha: 0.3,
  /** Tope de cambio por recalibracion: evita saltos por una semana rara. */
  maxChangePct: 0.1,
} as const;

export interface CalibrationInput {
  currentTdeeKcal: number;
  /** Media diaria de kcal registradas en la ventana. */
  meanIntakeKcal: number;
  /** Pendiente de la tendencia de peso, en kg por dia. */
  weightTrendKgPerDay: number;
  daysWithData: number;
  /** Dias con registro completo / dias de la ventana. */
  adherence: number;
  weightsPerWeek: number;
  /** El objetivo nutricional cambio dentro de la ventana de calibracion. */
  goalChangedInWindow: boolean;
}

export interface CalibrationResult {
  tdeeKcal: number;
  source: 'formula' | 'adaptive';
  recalibrated: boolean;
  /** TDEE que sugieren los datos antes de suavizar y recortar. */
  observedKcal: number | null;
  clamped: boolean;
  reasonCodes: string[];
}

/**
 * Corrige el TDEE con lo observado, o explica por que no lo hace.
 *
 * Devolver el numero de partida cuando los datos no dan para mas no es
 * conformismo: recalibrar con 6 dias y media adherencia produce un objetivo
 * peor que el de la formula, y ademas con apariencia de estar medido.
 */
export function calibrateTdee(input: CalibrationInput): CalibrationResult {
  const reasonCodes: string[] = [];

  if (input.daysWithData < CALIBRATION_RULES.minDays) reasonCodes.push('calibration_needs_days');
  if (input.adherence < CALIBRATION_RULES.minAdherence) {
    reasonCodes.push('calibration_low_adherence');
  }
  if (input.weightsPerWeek < CALIBRATION_RULES.minWeightsPerWeek) {
    reasonCodes.push('calibration_few_weights');
  }
  if (input.goalChangedInWindow) reasonCodes.push('calibration_goal_changed');

  if (reasonCodes.length > 0) {
    return {
      tdeeKcal: Math.round(input.currentTdeeKcal),
      source: 'formula',
      recalibrated: false,
      observedKcal: null,
      clamped: false,
      reasonCodes,
    };
  }

  const observed =
    input.meanIntakeKcal - input.weightTrendKgPerDay * ENERGY_CONSTANTS.kcalPerKgTissue;
  const suavizado =
    CALIBRATION_RULES.ewmaAlpha * observed +
    (1 - CALIBRATION_RULES.ewmaAlpha) * input.currentTdeeKcal;

  const maxKcal = input.currentTdeeKcal * (1 + CALIBRATION_RULES.maxChangePct);
  const minKcal = input.currentTdeeKcal * (1 - CALIBRATION_RULES.maxChangePct);
  const limitado = Math.min(Math.max(suavizado, minKcal), maxKcal);
  const clamped = limitado !== suavizado;

  return {
    tdeeKcal: Math.round(limitado),
    source: 'adaptive',
    recalibrated: true,
    observedKcal: Math.round(observed),
    clamped,
    reasonCodes: clamped ? ['calibration_change_capped'] : ['calibration_applied'],
  };
}

/** Ritmo semanal de cambio de peso, en % del peso corporal. */
export function weeklyWeightChangePct(trendKgPerDay: number, bodyWeightKg: number): number {
  if (bodyWeightKg <= 0) throw new Error('Peso corporal invalido');
  return roundTo(((trendKgPerDay * 7) / bodyWeightKg) * 100, 3);
}
